import { useMemo, useSyncExternalStore } from 'react';
import type { LayoutElement } from '../types';
import { useAppSelector } from './redux';
import { liveOhtRuntimeStore } from '../store/liveOhtRuntimeStore';
import { replaySnapshotStore } from '../store/replaySnapshotStore';
import {
  buildElementPortOwnership,
  dockFractionsAlongFace,
  portsOf,
} from '../components/canvas/amhs/Equipment/equipmentPortOwnership';
import {
  resolveEquipmentFoupCapacity,
  resolveEquipmentFoupSlots,
  type EquipmentFoupSlotView,
} from '../components/canvas/amhs/Equipment/equipmentFoupSlots';
import {
  getFabProcessToolPortPoses,
  resolveFabToolLayout,
} from '../components/canvas/amhs/Equipment/FabProcessTool/fabToolLayout';
import type { EquipmentPortVisual } from '../components/canvas/amhs/Equipment/equipmentPortTransfer';
import type { Port } from '../utils/ports';

const KIT_HEIGHT = 5;

function emptyVisuals(count: number): EquipmentPortVisual[] {
  return Array.from({ length: Math.max(1, count) }, (_, i) => ({
    portIndex: i,
    shutterOpen: 0,
    shutterState: 'closed' as const,
    transferState: 'idle' as const,
    occupancy: 'empty' as const,
    foupMode: 'hidden' as const,
    foupPosition: null,
    isLocked: false,
  }));
}

/**
 * Canonical equipment FOUP occupancy for 2D layout equipment.
 * Shares the same slot resolver as 3D {@link useEquipmentPortTransfer}.
 */
export function useEquipmentFoupSlotView(
  element: LayoutElement | null,
  layoutPorts: readonly Port[]
): EquipmentFoupSlotView | undefined {
  const running = useAppSelector((s) => s.simulation.running);
  const source = useAppSelector((s) => s.simulation.source);
  const hasPorts = running && Boolean(element) && layoutPorts.length > 0;
  const simTimeS = useAppSelector((s) => (hasPorts ? s.simulation.simulationTime : 0));
  const toolPickupReady = useAppSelector((s) => (hasPorts ? s.simulation.live.toolPickupReady : undefined));
  const equipmentInstances = useAppSelector((s) => s.equipment.instances);

  const liveTick = useSyncExternalStore(
    (notify) =>
      source === 'live' && hasPorts
        ? liveOhtRuntimeStore.subscribe(() => notify())
        : () => {},
    () => (hasPorts ? liveOhtRuntimeStore.getFrame().globalSimTimeS : 0),
    () => 0
  );
  const replayTick = useSyncExternalStore(
    (notify) => (hasPorts ? replaySnapshotStore.subscribe(notify) : () => {}),
    () => (hasPorts ? (replaySnapshotStore.getSnapshot()?.simTime ?? 0) : 0),
    () => 0
  );

  return useMemo(() => {
    if (!running || !element || layoutPorts.length === 0) return undefined;
    void liveTick;
    void replayTick;

    const portCount = Math.max(1, layoutPorts.length);
    const width = element.dimensions.width;
    const depth = element.dimensions.height;
    const ownership = buildElementPortOwnership(element, equipmentInstances);
    const instanceCapacity = equipmentInstances[element.id]?.foupCapacity;
    const capacity = resolveEquipmentFoupCapacity(element, portCount, instanceCapacity);
    const fractions = dockFractionsAlongFace(portsOf(element, equipmentInstances), portCount);
    const layout = resolveFabToolLayout(width, KIT_HEIGHT, depth);
    const poses = getFabProcessToolPortPoses(layout, portCount, fractions);

    const snapshot = replaySnapshotStore.getSnapshot();
    const liveFrame = source === 'live' ? liveOhtRuntimeStore.getFrame() : null;
    const liveRuntime = liveFrame
      ? liveOhtRuntimeStore.getActiveTransfer({
          portIds: ownership.portIndexById.keys(),
          stationAliases: ownership.ownerIds,
        })
      : null;
    const station = [...ownership.ownerIds]
      .map((ownerId) => snapshot?.equipment[ownerId])
      .find(Boolean);

    const transfer = liveRuntime?.transfer ?? (source === 'live' ? undefined : station?.activeTransfer);
    const animatingFoupId = transfer?.foupId ?? null;

    return resolveEquipmentFoupSlots({
      ownership,
      portCount,
      capacity,
      poses,
      layout,
      portVisuals: emptyVisuals(portCount),
      source: source === 'live' ? 'live' : 'replay',
      simTimeS,
      toolPickupReady: toolPickupReady ?? [],
      livePortFoupIds: liveFrame?.portFoupIds ?? null,
      replayPorts: snapshot?.ports,
      animatingFoupId,
    });
  }, [
    running,
    element,
    layoutPorts.length,
    equipmentInstances,
    source,
    simTimeS,
    toolPickupReady,
    liveTick,
    replayTick,
  ]);
}
